import { Box, ButtonBase, IconButton, Theme, useTheme } from '@mui/material';
import { ReactElement, type Ref } from 'react';
import { ColoredIcon } from '../../ColoredIconAndChip';
import { type Dot, dotWindow } from './dotWindow';

// The colour shift of a dot or a step button on hover. Not at all for a person who asked for
// reduced motion.
const colourShift = (theme: Theme) => ({
  transition: `background-color ${theme.requestBanner.stateTransitionDuration}`,
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
});

const focusRing = (theme: Theme) => ({
  '&.Mui-focusVisible': {
    outline: `${theme.semantic.spacing['3xs']} solid ${theme.palette.primary.main}`,
    outlineOffset: theme.semantic.spacing['3xs'],
  },
});

type StepButtonProps = {
  direction: 'back' | 'next';
  label: string;
  onClick: () => void;
};

// The identity row's arrow look, in the banner: a round 32px button on the paper colour, with the
// Carousel's rounded chevron (24px) in the interactive fill colour. It has no lifted shadow,
// because the banner is already the raised surface. Hover tints it with the primary subtle tone.
function StepButton({ direction, label, onClick }: Readonly<StepButtonProps>): ReactElement {
  const theme = useTheme();
  const iconName = direction === 'back' ? 'arrowLeftRounded' : 'arrowRightRounded';
  return (
    <IconButton
      aria-label={label}
      data-action={`banner-${direction}`}
      onClick={onClick}
      disableRipple
      sx={(theme: Theme) => ({
        width: theme.semantic.size['6xs'],
        height: theme.semantic.size['6xs'],
        padding: 0,
        backgroundColor: theme.palette.background.paper,
        ...colourShift(theme),
        '&:hover': { backgroundColor: theme.requestBanner.stepHoverBackground },
        ...focusRing(theme),
      })}
    >
      <ColoredIcon
        iconName={iconName}
        iconSize="medium"
        // ColoredIcon grows to fill its parent by default. The board's chevron stays 24px. It takes
        // a plain style object: a style function would be dropped.
        sx={{ flex: 'none', fill: theme.palette.interactive.enabled.fill }}
      />
    </IconButton>
  );
}

// The dot: 8px, or 5px at a window end while more requests lie beyond it. The request on show is
// the permanent primary colour. The others take the default border colour, and the primary colour
// under the pointer.
const dotSx = (dot: Dot) => (theme: Theme) => {
  const size = dot.isEdge ? theme.requestBanner.edgeDotSize : theme.requestBanner.dotSize;
  return {
    width: size,
    height: size,
    borderRadius: theme.semantic.borderRadius.round,
    backgroundColor: dot.isCurrent ? theme.palette.permanent.primary : theme.requestBanner.dotColor,
    ...colourShift(theme),
    ...(dot.isCurrent
      ? {}
      : { '.MuiButtonBase-root:hover > &': { backgroundColor: theme.palette.primary.main } }),
  };
};

type DotButtonProps = {
  dot: Dot;
  label: string;
  onClick: () => void;
  buttonRef: Ref<HTMLButtonElement> | undefined;
};

// A small dot inside a larger hit area, so the dot can stay small and still be a target.
function DotButton({ dot, label, onClick, buttonRef }: Readonly<DotButtonProps>): ReactElement {
  return (
    <ButtonBase
      ref={buttonRef}
      aria-label={label}
      aria-current={dot.isCurrent ? 'true' : undefined}
      data-action="banner-dot"
      onClick={onClick}
      disableRipple
      sx={(theme: Theme) => ({
        width: theme.semantic.size['7xs'],
        height: theme.semantic.size['7xs'],
        borderRadius: theme.semantic.borderRadius.round,
        ...focusRing(theme),
      })}
    >
      <Box component="span" aria-hidden sx={dotSx(dot)} />
    </ButtonBase>
  );
}

export type SteppingControlProps = {
  /** The request on show, counted from 1. */
  step: number;
  total: number;
  backLabel: string;
  nextLabel: string;
  dotLabel: (position: number, total: number) => string;
  onBack: () => void;
  onNext: () => void;
  onJump: (position: number) => void;
  /** For the dot of the request on show. */
  currentDotRef: Ref<HTMLButtonElement>;
};

/**
 * Back and next either side of the dots, one dot for each open request while there are at most
 * five and a sliding window of five above that. The parts are fixed in size: a dot has a 24px hit
 * area whatever its drawn size, so the row of dots keeps its width as the window slides.
 */
export function SteppingControl({
  step,
  total,
  backLabel,
  nextLabel,
  dotLabel,
  onBack,
  onNext,
  onJump,
  currentDotRef,
}: Readonly<SteppingControlProps>): ReactElement {
  return (
    <Box
      sx={(theme: Theme) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.semantic.spacing['2xs'],
      })}
    >
      <StepButton direction="back" label={backLabel} onClick={onBack} />
      {dotWindow(step, total).map((dot) => (
        <DotButton
          key={dot.position}
          dot={dot}
          label={dotLabel(dot.position, total)}
          onClick={() => onJump(dot.position)}
          buttonRef={dot.isCurrent ? currentDotRef : undefined}
        />
      ))}
      <StepButton direction="next" label={nextLabel} onClick={onNext} />
    </Box>
  );
}
